import {
  $applyNodeReplacement,
  type DOMConversionMap,
  type DOMConversionOutput,
  type DOMExportOutput,
  type EditorConfig,
  type LexicalNode,
  type NodeKey,
  type SerializedTextNode,
  type Spread,
  TextNode,
} from 'lexical'

export type SerializedMentionNode = Spread<
  {
    mentionName: string
    mentionValue: string
  },
  SerializedTextNode
>

function convertMentionElement(domNode: HTMLElement): DOMConversionOutput | null {
  const textContent = domNode.textContent
  domNode.classList.add('mention')
  const valueContent = domNode.getAttribute('data-lexical-mention-value')
  if (valueContent !== null && textContent != null) {
    const node = $createMentionNode(textContent, valueContent)
    return { node }
  }

  return null
}

export class MentionNode extends TextNode {
  __mentionName: string
  __mentionValue: string

  static getType(): string {
    return 'mention'
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode(node.__mentionName, node.__mentionValue, node.__text, node.__key)
  }
  static importJSON(serializedNode: SerializedMentionNode): MentionNode {
    const node = $createMentionNode(serializedNode.mentionName, serializedNode.mentionValue)
    node.setTextContent(serializedNode.text)
    node.setFormat(serializedNode.format)
    node.setDetail(serializedNode.detail)
    node.setMode(serializedNode.mode)
    node.setStyle(serializedNode.style)
    return node
  }

  constructor(mentionName: string, mentionValue: string, text?: string, key?: NodeKey) {
    super(text ?? mentionName, key)
    this.__mentionName = mentionName
    this.__mentionValue = mentionValue
  }

  exportJSON(): SerializedMentionNode {
    return {
      ...super.exportJSON(),
      mentionName: this.__mentionName,
      mentionValue: this.__mentionValue,
      type: 'mention',
      version: 1,
    }
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config)
    dom.setAttribute('data-lexical-mention', 'true')
    dom.setAttribute('data-lexical-mention-value', this.__mentionValue)
    dom.className = 'mention'
    return dom
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('span')
    element.classList.add('mention')
    element.setAttribute('data-lexical-mention', 'true')
    element.setAttribute('data-lexical-mention-value', this.__mentionValue)
    element.textContent = this.__text
    return { element }
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-lexical-mention')) {
          return null
        }

        if (!domNode.hasAttribute('data-lexical-mention-value')) {
          return null
        }
        return {
          conversion: convertMentionElement,
          priority: 1,
        }
      },
    }
  }

  isTextEntity(): true {
    return true
  }

  canInsertTextBefore(): boolean {
    return false
  }

  canInsertTextAfter(): boolean {
    return false
  }
}

export function $createMentionNode(mentionName: string, mentionValue: string): MentionNode {
  const mentionNode = new MentionNode(mentionName, mentionValue)
  mentionNode.setMode('segmented').toggleDirectionless()
  return $applyNodeReplacement(mentionNode)
}

export function $isMentionNode(node: LexicalNode | null | undefined): node is MentionNode {
  return node instanceof MentionNode
}
